import { LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID, WritableSignal, inject, makeStateKey, signal } from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { PrimeNGConfig } from 'primeng/api';
import { BehaviorSubject, catchError, filter, first, map, of, take, throwError, Observable } from 'rxjs';

import { Environment, ENVIRONMENT } from '@environment';
import { LanguageService } from '@usheru-hff/sdk/language';
import { getLocaleFromUrl } from '@usheru-hff/sdk/location';
import { UsheruApi } from '@usheru-hff/shared/data-access-backend';
import { Channel, Language, LanguageListWrapper } from '@usheru-hff/shared/domain';

import { primeEN } from '../entities/languages/primeng/en';
import { primeES } from '../entities/languages/primeng/es';
import { TranslationsBundle } from '../entities/translations.model';

const CACHESPrime = {
  en: primeEN,
  nl: primeEN,
  fr: primeEN,
  es: primeES,
  de: primeEN,
  pt: primeEN,
  zn: primeEN,
  it: primeEN,
  no: primeEN,
  ca: primeEN,
  pl: primeEN,
  da: primeEN,
  cs: primeEN,
  ro: primeEN,
  sv: primeEN,
  hu: primeEN,
  el: primeEN,
  ru: primeEN,
  uk: primeEN,
  bg: primeEN
};

const DEFAULT_LANGUAGE: Language = {
  id: 1,
  created: 1520020338000,
  name: 'English',
  englishName: 'English',
  code: 'en',
  code3: 'eng',
  slug: 'english',
  trMsg: 'language.name.english',
  default: true
};

@Injectable({
  providedIn: 'root'
})
export class TranslationsService {
  private readonly transferState = inject(TransferState);

  // default translations; they get overridden by app specific translations
  /**
   * @description Stores the translations for the app. If the translations
   * are not found in the cache, they are fetched from the Transfer State.
   * Could be some cases where the client side cache is not available. Maybe
   * this should be fixed in another way.
   */
  _CACHES: TranslationsBundle;
  get CACHES(): TranslationsBundle {
    return this._CACHES;
  }

  set CACHES(x: TranslationsBundle) {
    this._CACHES = x;
  }

  language$ = new BehaviorSubject<Language>(null);

  channelLanguages: Language[] = [];

  private _language: Language;
  get language(): Language {
    return this._language;
  }

  set language(x: Language) {
    this._language = x;
    this.env.language = x?.code;
    this.language$.next(x);
    this.setLanguageAttribute(x);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultParams: any = {};
  translateQueue: { key; defaultValue; params }[] = [];

  set channel(channel: Channel) {
    this.defaultParams.channel = channel.name;
  }

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private usheruApi: UsheruApi,
    private primeconfig: PrimeNGConfig,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(LOCALE_ID) private localeId: string,
    private locationStrategy: LocationStrategy,
    private languageService: LanguageService
  ) {}

  updateTranslationLanguage(): Promise<void> {
    const language = this.extractLanguagePrefix();
    if (this.env.appType !== 'widget') {
      return this.setTranslationFromApi(language).then(() => Promise.resolve());
    }
    return Promise.resolve();
  }

  public init(): Promise<void> {
    return Promise.resolve(this.updateTranslationLanguage());
  }

  private extractLanguagePrefix(): Language {
    const localeCode = getLocaleFromUrl(this.locationStrategy);
    return this.getUrlLanguage(localeCode) || this.getDefaultLanguage();
  }

  private getUrlLanguage(langCode: string): Language | undefined {
    return this.getLanguageFromChannel(langCode);
  }

  private getDefaultLanguage() {
    return this.channelLanguages[0] || DEFAULT_LANGUAGE;
  }

  setTranslationFromApi(language: Language): Promise<void> {
    return new Promise<void>(resolve => {
      this.getTranslation(language).subscribe(translateData => {
        this.CACHES = translateData;
        this.language = language;
        this.setPrimeNgLanguage(this.language?.code);
        resolve();
      });
    });
  }

  isLanguageAllowedForChannel(lanCode: string): boolean {
    return this.channelLanguages.map(lan => lan.code).includes(lanCode);
  }

  getLanguageFromChannel(lanCode: string): Language | undefined {
    //console.log('this.channelLanguages', this.channelLanguages);
    return lanCode && this.channelLanguages.find(language => language.code === lanCode);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getTranslation(language: Language): Observable<any> {
    return this.getTranslationsApi({ languages: [language?.slug] });
  }

  setLanguage(language: Language): void {
    const CACHES = this.CACHES;
    if (Object.keys(CACHES).includes(language.code)) {
      this.language = language;
    } else {
      this.language = DEFAULT_LANGUAGE;
    }

    this.setPrimeNgLanguage(this.language.code);
  }

  setPrimeNgLanguage(languageKey: string) {
    this.primeconfig.setTranslation(CACHESPrime[languageKey]);
  }

  onTranslationsRetrieved() {
    if (this.translateQueue.length > 0) {
      this.translateQueue.forEach(obj => this.translate(obj.key, obj.defaultValue, obj.params));
    }
  }

  keyExists(key: string): boolean {
    return !!this.CACHES[this.language?.code] && !!this.CACHES[this.language?.code][key];
  }

  translate(key: string, defaultValue?: string, params = {}): string {
    let langcode = this.language?.code || 'en';

    // we can manually use a language code we want to use (only in develop environment)
    if (this.env.manualLanguageCode && this.env.mode == 'develop') {
      langcode = this.env.manualLanguageCode;
    }

    const CACHES = this.CACHES;

    if (!CACHES) return key;

    let translation;

    if (CACHES[langcode] && CACHES[langcode][key]) {
      translation = CACHES[langcode][key];
    } else if (defaultValue || defaultValue === '') {
      translation = defaultValue;
      if (this.isTranslationsWarnsEnabled) {
        console.warn(`Language ${langcode}. Translation missing. Used text: ${key}`);
      }
    } else {
      translation = key;
      if (this.isTranslationsWarnsEnabled) {
        console.error(`Language ${langcode}. Translation missing for language for key: ${key}`);
      }
    }

    let translatedValue = translation === '' ? '' : translation || defaultValue || key;

    params = { ...this.defaultParams, ...params };
    const keys = Object.keys(params);
    keys.forEach(key => {
      translatedValue = translatedValue?.replace(`_${key}_`, params[key]);
    });

    return translatedValue;
  }

  currentLanguage(): Language {
    return this.language;
  }

  private getTranslationsApi(filter: LanguageListWrapper): Observable<Language[]> {
    // return this.transferState.useScullyTransferState(
    //   'languages',

    return this.http.get<Language[]>(`${this.env.api.url}/translations/`, {
      headers: this.usheruApi.getStandardHeader(),
      params: this.usheruApi.filterToParams(filter)
    });
    // );
  }

  get isTranslationsWarnsEnabled(): boolean {
    return this.env.mode === 'test';
  }

  setLanguageAttribute(lang: Language) {
    if (this.env.appType === 'widget') return;
  }
}
